import { CUANTOS_POR_AREA, porcentajeDelPack } from "./packs-de-area"
import {
  MODULOS_VENDIBLES,
  NUCLEO_CENTIMOS_MENSUALES,
  PRECIOS_POR_MODULO,
  centimosDelIntervalo,
  type AreaDeModulo,
  type Intervalo,
  type ModuloVendible,
} from "./precios"

/**
 * Lo que cuesta una empresa al mes (o al año), a partir de los modulos que tiene
 * encendidos y su plantilla. Aritmetica pura: ni Supabase, ni Stripe, ni sesion.
 *
 * Es la unica fuente del numero. El informe de la migracion, la pantalla de
 * suscripcion y el importe que se le manda a Stripe salen todos de aqui, para que
 * no haya dos cuentas que puedan discrepar.
 */
export interface LineaDeFactura {
  concepto: "nucleo" | ModuloVendible
  area: AreaDeModulo | null
  cantidad: number
  centimosUnitarios: number
  centimosLinea: number
}

export interface DescuentoDeArea {
  area: AreaDeModulo
  porcentaje: number
  centimos: number
}

export interface FacturaDeModulos {
  lineas: LineaDeFactura[]
  descuentos: DescuentoDeArea[]
  totalCentimos: number
}

/**
 * En que orden se listan los descuentos. Es un `Record<AreaDeModulo, number>` y
 * no una lista a proposito: asi el compilador exige que TODA area tenga su
 * sitio, y meter un area nueva en `lib/modules/areas.ts` obliga a pasar por
 * aqui. Con una lista, el area nueva se quedaba sin pack en silencio.
 */
const ORDEN_POR_AREA: Record<AreaDeModulo, number> = {
  equipo: 0,
  operaciones: 1,
  ventas: 2,
  direccion: 3,
}

const ORDEN_DE_AREAS: AreaDeModulo[] = (Object.keys(ORDEN_POR_AREA) as AreaDeModulo[]).sort(
  (a, b) => ORDEN_POR_AREA[a] - ORDEN_POR_AREA[b],
)

// El umbral del pack y el recuento por area viven en `packs-de-area.ts`, que es
// de donde los toman tambien la pantalla y lo que se le manda a Stripe. Estaban
// aqui primero, cuando esta era la unica cuenta; con tres copias de una regla de
// precio, un dia dicen cosas distintas.

export function calcularFactura(params: {
  modulos: readonly string[]
  empleados: number
  intervalo: Intervalo
}): FacturaDeModulos {
  const { empleados, intervalo } = params

  // Lo que llega puede traer claves viejas de la base (`cierre_caja`, `personal`):
  // se ignoran en vez de reventar, porque esto calcula una factura y no valida un
  // catalogo.
  const encendidos = [...new Set(params.modulos)].filter((m): m is ModuloVendible =>
    (MODULOS_VENDIBLES as readonly string[]).includes(m),
  )

  const lineas: LineaDeFactura[] = [
    {
      concepto: "nucleo",
      area: null,
      cantidad: 1,
      centimosUnitarios: centimosDelIntervalo(NUCLEO_CENTIMOS_MENSUALES, intervalo),
      centimosLinea: centimosDelIntervalo(NUCLEO_CENTIMOS_MENSUALES, intervalo),
    },
  ]

  for (const modulo of MODULOS_VENDIBLES) {
    if (!encendidos.includes(modulo)) continue
    const precio = PRECIOS_POR_MODULO[modulo]
    const cantidad = precio.tipo === "por_empleado" ? Math.max(0, empleados) : 1
    const centimosUnitarios = centimosDelIntervalo(precio.centimosMensuales, intervalo)
    lineas.push({
      concepto: modulo,
      area: precio.area,
      cantidad,
      centimosUnitarios,
      centimosLinea: centimosUnitarios * cantidad,
    })
  }

  const descuentos: DescuentoDeArea[] = []
  for (const area of ORDEN_DE_AREAS) {
    const deEsaArea = lineas.filter((l) => l.area === area)
    const porcentaje = porcentajeDelPack(deEsaArea.length, CUANTOS_POR_AREA[area])
    if (porcentaje === 0) continue
    const base = deEsaArea.reduce((suma, l) => suma + l.centimosLinea, 0)
    // Hacia abajo: el redondeo siempre a favor de quien paga.
    const centimos = Math.floor((base * porcentaje) / 100)
    if (centimos > 0) descuentos.push({ area, porcentaje, centimos })
  }

  const bruto = lineas.reduce((suma, l) => suma + l.centimosLinea, 0)
  const rebaja = descuentos.reduce((suma, d) => suma + d.centimos, 0)

  return { lineas, descuentos, totalCentimos: bruto - rebaja }
}
